import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FacebookLogo, GoogleLogo } from '../components/icons';
import type { LegacyLoginConfig, SignInProvider } from '../legacy-api/chat';

import { useAuth } from './AuthProvider';
import { loadFacebook, loadGoogle, SignInCancelledError } from './providers';

export interface SignInPanelProps {
  config: LegacyLoginConfig;
  title: string;
  /** Why signing in is needed here: to chat, to enter the giveaway. */
  body: string;
}

/** Google and Facebook sign-in, for the features the legacy API runs. */
export function SignInPanel({ config, title, body }: SignInPanelProps): ReactElement {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [ready, setReady] = useState<Record<SignInProvider, boolean>>({
    google: false,
    facebook: false,
  });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const google = config.googleClientId ?? '';
  const facebook = config.fbAppId ?? '';

  // Loaded here, not on click: the sign-in popup must open within the click.
  useEffect(() => {
    if (google !== '') {
      void loadGoogle().then(() => {
        setReady((current) => ({ ...current, google: true }));
      });
    }

    if (facebook !== '') {
      void loadFacebook().then(() => {
        setReady((current) => ({ ...current, facebook: true }));
      });
    }
  }, [google, facebook]);

  const start = (provider: SignInProvider, providerId: string): void => {
    setBusy(true);
    setFailed(false);
    signIn(provider, providerId)
      .catch((cause: unknown) => {
        // Closing the popup is a choice, not a failure.
        if (!(cause instanceof SignInCancelledError)) {
          setFailed(true);
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const buttonClass =
    'flex w-full items-center justify-center gap-3 rounded-xl py-3.5 font-semibold transition-opacity disabled:opacity-50';

  return (
    <div className="mx-auto max-w-sm space-y-3 py-6 text-center">
      <h2 className="text-lg font-bold text-fog-100">{title}</h2>
      <p className="text-sm text-fog-500">{body}</p>
      {failed && (
        <p role="alert" className="text-sm text-red-400">
          {t('chat.signInFailed')}
        </p>
      )}
      {google !== '' && (
        <button
          type="button"
          disabled={!ready.google || busy}
          onClick={() => {
            start('google', google);
          }}
          // White with Google's grey outline and near-black text, per its
          // sign-in branding guidelines for a light button.
          className={`${buttonClass} border border-[#747775] bg-white text-[#1f1f1f]`}
        >
          <GoogleLogo className="size-5" />
          {t('chat.signInGoogle')}
        </button>
      )}
      {facebook !== '' && (
        <button
          type="button"
          disabled={!ready.facebook || busy}
          onClick={() => {
            start('facebook', facebook);
          }}
          className={`${buttonClass} bg-[#1877f2] text-white`}
        >
          <FacebookLogo className="size-5" />
          {t('chat.signInFacebook')}
        </button>
      )}
    </div>
  );
}
